import { useRouter } from "next/router";
import Script from "next/script";
import React, { useCallback, useEffect } from "react";

import { HINOME_ENTERPRISE_PATH, HINOME_REPLY_PATH, HINOME_SNS_DAIKOU_PATH } from "@/global-state/external-path";
import { firebaseGetAuth } from "@/global-state/firebase-settings";
import { authUserAtom, instagramAccountInfoAtom } from "@/global-state/jotai-atoms";

import { useAtomValue } from "jotai";

// widget.js が読み取る、ログイン中ユーザーの文脈。
declare global {
  interface Window {
    HINOME_CHAT_CONTEXT?: {
      name: string;
      email: string;
      instagramId?: string;
      page: string;
      // 過去のやり取りを本人にだけ見せるための ID トークン。widget が呼ぶたびに
      // Firebase SDK が期限切れを判定して取り直すので、関数のまま渡す。
      getIdToken?: () => Promise<string | null>;
      // 開始メニューの下に並べるリンク（新しいタブで開く）
      links?: { label: string; url: string }[];
      linksTitle?: string;
    };
  }
}

// サイドメニューにあった「もっと活用する」。自社チャットでは、チャットの最初のメニューの下に出す
const MORE_LINKS = [
  { label: "HINOME Reply", url: HINOME_REPLY_PATH },
  { label: "アカウント横断分析", url: HINOME_ENTERPRISE_PATH },
  { label: "運用代行サービス", url: HINOME_SNS_DAIKOU_PATH },
];

// 回答のお知らせメールの「チャットを開く」は ?hc_open=相談ID&hc_key=合言葉 で来る。
// 未ログインだとログイン画面へ送り直され、そのとき URL から落ちてしまう。widget.js が読み込まれる前、
// アプリの読み込み時点で控えておき、widget.js 側が sessionStorage (hinome_chat_link) から拾う
if (typeof window !== "undefined") {
  try {
    const query = new URLSearchParams(window.location.search);
    const id = query.get("hc_open");
    const key = query.get("hc_key");
    if (id && key) sessionStorage.setItem("hinome_chat_link", JSON.stringify({ id, key }));
  } catch {
    // 保存できなくても、URL が残っていれば widget.js がそこから読む
  }
}

// HINOME 自社チャットサポート。widget.js 自体が body 直下にボタンとパネルを生成する
// 自己完結スクリプトなので、このコンポーネントはスクリプトを読み込むだけで DOM を持たない。
// 配信元は hinome-chat (Cloud Run) で、URL は環境ごとに CHAT_WIDGET_URL で与える。
const AppChatWidget: React.FC = () => {
  const src = process.env.CHAT_WIDGET_URL;
  const authUser = useAtomValue(authUserAtom);
  const instagram = useAtomValue(instagramAccountInfoAtom);
  const router = useRouter();

  // ID トークンは 1 時間で失効する。widget が過去のやり取りを開くその時に取り直せるよう、
  // 文字列ではなく取得関数を渡す。chat 側 (server.js) が verifyIdToken で検証する。
  const getIdToken = useCallback(async () => {
    try {
      return (await firebaseGetAuth.currentUser?.getIdToken()) ?? null;
    } catch {
      return null;
    }
  }, []);

  // ログイン中は名前・メール・選択中の Instagram アカウント・閲覧ページを widget へ渡す。
  // widget 側は入力欄を埋めたうえでそのままチャットを開始する。未ログインなら何も置かず、
  // 従来どおり名前とメールを入力してもらう。
  //
  // 置き換えたあとにイベントで知らせる。widget はこれを受けて「誰のスレッドを出すか」を
  // 見直す (ログアウトすれば前の人の会話を画面から外し、別の人でログインすればその人の
  // 会話を出す)。widget が先に読み込まれていて、認証が後から解決するケースのための合図。
  useEffect(() => {
    if (!src) return;
    window.HINOME_CHAT_CONTEXT = authUser
      ? {
          name: `${authUser.last_name ?? ""} ${authUser.first_name ?? ""}`.trim(),
          email: authUser.email,
          instagramId: instagram?.account_name,
          page: router.asPath,
          getIdToken,
          links: MORE_LINKS,
          linksTitle: "もっと活用する",
        }
      : undefined;
    window.dispatchEvent(new CustomEvent("hinome-chat:context"));
  }, [src, authUser, instagram, router.asPath, getIdToken]);

  if (!src) return null;

  // afterInteractive は hydration 後に body へ挿入される。問い合わせ導線なので
  // load イベントまで待つ lazyOnload ではなくこちらを使う。
  return <Script id="hinome-chat-widget" src={src} strategy="afterInteractive" />;
};

export default React.memo(AppChatWidget);
